import { useState, useMemo } from "react";
import { useCompanyUsers, useUpdateCompanyUser, type CompanyUser } from "@/hooks/useCompanyUsers";
import { useCompanyRoles } from "@/hooks/useCompanyRoles";
import { useProjects } from "@/hooks/useProjects";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/shared/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Plus, Search, UserPlus, Mail, RotateCcw } from "lucide-react";
import { format } from "date-fns";

const statusColors: Record<string, string> = {
  active: "bg-primary/15 text-primary border-primary/30",
  invited: "bg-warning/15 text-warning border-warning/30",
  inactive: "bg-muted text-muted-foreground border-muted",
  suspended: "bg-destructive/15 text-destructive border-destructive/30",
};

export const UsersTab = () => {
  const { user } = useAuth();
  const { data: users = [], isLoading } = useCompanyUsers();
  const { data: roles = [] } = useCompanyRoles();
  const { data: projects = [] } = useProjects();
  const updateUser = useUpdateCompanyUser();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<CompanyUser | null>(null);
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  // Invite form state
  const [invFirstName, setInvFirstName] = useState("");
  const [invLastName, setInvLastName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invPhone, setInvPhone] = useState("");
  const [invTitle, setInvTitle] = useState("");
  const [invRoleId, setInvRoleId] = useState("");
  const [invProjectIds, setInvProjectIds] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);

  const filtered = users.filter((u) => {
    if (filterRole !== "all" && u.role_id !== filterRole) return false;
    if (filterStatus !== "all" && u.status !== filterStatus) return false;
    if (search) {
      const s = search.toLowerCase();
      const match = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(s);
      if (!match) return false;
    }
    return true;
  });

  const userColumns = useMemo<ColumnDef<CompanyUser, any>[]>(() => [
    {
      id: "user",
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
      header: "User",
      size: 200,
      cell: ({ row }) => {
        const u = row.original;
        return (
          <div className="flex items-center gap-2">
            <Avatar className="h-7 w-7">
              <AvatarImage src={u.profile_photo_url || undefined} />
              <AvatarFallback className="text-[10px]">{u.first_name?.[0]}{u.last_name?.[0]}</AvatarFallback>
            </Avatar>
            <span className="font-medium truncate">{u.first_name} {u.last_name}</span>
          </div>
        );
      },
    },
    { accessorKey: "email", header: "Email", size: 220 },
    { accessorKey: "title", header: "Title", size: 140, cell: ({ getValue }) => getValue() || "—" },
    {
      id: "role",
      accessorFn: (row) => row.roles?.name || "",
      header: "Role",
      size: 120,
      cell: ({ row }) => {
        const r = row.original.roles;
        return r ? (
          <Badge variant="outline" style={{ borderColor: r.color, color: r.color }} className="text-[10px]">
            {r.name}
          </Badge>
        ) : "—";
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 110,
      cell: ({ getValue }) => {
        const s = getValue() as string;
        return (
          <Badge variant="outline" className={statusColors[s] || ""}>
            {s === "invited" ? "⏳ Invited" : s.charAt(0).toUpperCase() + s.slice(1)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "last_login",
      header: "Last Login",
      size: 170,
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? (
          <span className="text-sm">{format(new Date(v), "MMM d, yyyy h:mm a")}</span>
        ) : (
          <span className="text-sm text-muted-foreground">Never</span>
        );
      },
    },
    {
      accessorKey: "invited_at",
      header: "Invited",
      size: 130,
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? <span className="text-sm">{format(new Date(v), "MMM d, yyyy")}</span> : "—";
      },
    },
    {
      accessorKey: "activated_at",
      header: "Activated",
      size: 130,
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? <span className="text-sm">{format(new Date(v), "MMM d, yyyy")}</span> : "—";
      },
    },
    {
      id: "actions",
      header: "",
      size: 80,
      enableSorting: false,
      cell: ({ row }) => {
        const u = row.original;
        return (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEditUser(u); }}>
            {u.status === "invited" ? "Resend" : "Edit"}
          </Button>
        );
      },
    },
  ], []);

  const selectedRole = roles.find((r) => r.id === invRoleId);
  const autoAssign = selectedRole?.permissions?.auto_assign_all_projects;

  const handleInvite = async () => {
    if (!invFirstName.trim() || !invLastName.trim() || !invEmail.trim() || !invRoleId) {
      toast.error("First name, last name, email, and role are required");
      return;
    }
    setInviting(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const selectedRoleName = roles.find((r) => r.id === invRoleId)?.name || "";

      const { data: result, error } = await supabase.functions.invoke("invite-user", {
        body: {
          email: invEmail.trim(),
          first_name: invFirstName.trim(),
          last_name: invLastName.trim(),
          role_id: invRoleId,
          role_name: selectedRoleName,
          invite_type: "company_user",
          company_id: companyId,
          project_ids: autoAssign ? [] : invProjectIds,
          phone: invPhone || null,
          title: invTitle || null,
        },
      });
      if (error) throw error;
      if (result?.error) throw new Error(result.error);

      toast.success(`Invite sent to ${invEmail}`);
      setInviteOpen(false);
      resetInviteForm();
    } catch (err: any) {
      toast.error(err.message || "Failed to invite user");
    } finally {
      setInviting(false);
    }
  };

  const resetInviteForm = () => {
    setInvFirstName("");
    setInvLastName("");
    setInvEmail("");
    setInvPhone("");
    setInvTitle("");
    setInvRoleId("");
    setInvProjectIds([]);
  };

  const handleUpdateUser = async (field: string, value: any) => {
    if (!editUser) return;
    try {
      await updateUser.mutateAsync({ id: editUser.id, [field]: value } as any);
      setEditUser((prev) => prev ? { ...prev, [field]: value } : null);
      toast.success("User updated");
    } catch (err: any) {
      toast.error(err.message || "Update failed");
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Users</h2>
        <Button size="sm" onClick={() => setInviteOpen(true)}>
          <UserPlus className="h-4 w-4 mr-1" /> Invite User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Spreadsheet-style user list */}
      <DataTable<CompanyUser>
        tableId="settings-users"
        columns={userColumns}
        data={filtered}
        isLoading={isLoading}
        emptyMessage="No users found"
        globalFilter={search}
        onRowClick={(u) => setEditUser(u)}
        enableFiltering={false}
        enableSorting
        enableResize
        enableReorder
        enableColumnToggle
      />

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invite New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={invFirstName} onChange={(e) => setInvFirstName(e.target.value)} /></div>
              <div><Label>Last Name *</Label><Input value={invLastName} onChange={(e) => setInvLastName(e.target.value)} /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="user@company.com" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={invPhone} onChange={(e) => setInvPhone(e.target.value)} /></div>
              <div><Label>Title</Label><Input value={invTitle} onChange={(e) => setInvTitle(e.target.value)} placeholder="e.g. Senior PM" /></div>
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={invRoleId} onValueChange={setInvRoleId}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                        {r.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {invRoleId && !autoAssign && (
              <div>
                <Label>Assign to Projects</Label>
                <div className="mt-1 max-h-36 overflow-y-auto space-y-1 border rounded-md p-2">
                  {projects.map((p: any) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={invProjectIds.includes(p.id)}
                        onCheckedChange={(checked) => {
                          setInvProjectIds((prev) =>
                            checked ? [...prev, p.id] : prev.filter((x) => x !== p.id)
                          );
                        }}
                      />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {invRoleId && autoAssign && (
              <p className="text-sm text-muted-foreground">✅ Auto-assigned to all projects (based on {selectedRole?.name} role)</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting}>
              {inviting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              <Mail className="h-4 w-4 mr-1" /> Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Sheet */}
      <Sheet open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <SheetContent className="w-[400px] overflow-y-auto">
          {editUser && (
            <>
              <SheetHeader><SheetTitle>{editUser.first_name} {editUser.last_name}</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={editUser.profile_photo_url || undefined} />
                    <AvatarFallback>{editUser.first_name[0]}{editUser.last_name[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{editUser.email}</p>
                    <Badge variant="outline" className={statusColors[editUser.status] || ""}>{editUser.status}</Badge>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First Name</Label><Input value={editUser.first_name} onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })} onBlur={() => handleUpdateUser("first_name", editUser.first_name)} /></div>
                  <div><Label>Last Name</Label><Input value={editUser.last_name} onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })} onBlur={() => handleUpdateUser("last_name", editUser.last_name)} /></div>
                </div>
                <div><Label>Phone</Label><Input value={editUser.phone || ""} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} onBlur={() => handleUpdateUser("phone", editUser.phone)} /></div>
                <div><Label>Title</Label><Input value={editUser.title || ""} onChange={(e) => setEditUser({ ...editUser, title: e.target.value })} onBlur={() => handleUpdateUser("title", editUser.title)} /></div>

                <div>
                  <Label>Role</Label>
                  <Select value={editUser.role_id} onValueChange={(v) => handleUpdateUser("role_id", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                            {r.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Status</Label>
                  <Select value={editUser.status} onValueChange={(v) => handleUpdateUser("status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />
                <div>
                  <Label>Notes</Label>
                  <Input value={editUser.notes || ""} onChange={(e) => setEditUser({ ...editUser, notes: e.target.value })} onBlur={() => handleUpdateUser("notes", editUser.notes)} />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      try {
                        const { error } = await supabase.auth.resetPasswordForEmail(editUser.email, {
                          redirectTo: "https://commandxprojectmanage.lovable.app/auth/reset-password",
                        });
                        if (error) throw error;
                        toast.success(`Password reset email sent to ${editUser.email}`);
                      } catch (err: any) {
                        toast.error(err.message || "Failed to send reset email");
                      }
                    }}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" /> Send Password Reset Email
                  </Button>

                  {editUser.status === "invited" && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={async () => {
                        try {
                          const { data: companyId } = await supabase.rpc("my_company_id");
                          const roleName = roles.find((r) => r.id === editUser.role_id)?.name || "";
                          const { error } = await supabase.functions.invoke("invite-user", {
                            body: {
                              email: editUser.email,
                              first_name: editUser.first_name,
                              last_name: editUser.last_name,
                              role_id: editUser.role_id,
                              role_name: roleName,
                              invite_type: "company_user",
                              company_id: companyId,
                              project_ids: [],
                            },
                          });
                          if (error) throw error;
                          toast.success(`Invite resent to ${editUser.email}`);
                        } catch (err: any) {
                          toast.error(err.message || "Failed to resend invite");
                        }
                      }}
                    >
                      <Mail className="h-4 w-4 mr-1" /> Resend Invite Email
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
